import type { ResultOf } from '@graphql-typed-document-node/core'
import { atom, useAtom, useAtomValue } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { useEffect, useMemo } from 'react'
import { type FragmentType, getFragmentData, graphql } from '~/lib/graphql'
import type { DateText, HourMinute, PlanId, RestaurantId } from '~/lib/graphql/scalars'
import { seatLabel } from '~/lib/seat/seat2'

export const PlanSeat = graphql(/* GraphQL */ `
  fragment PlanSeat on Seat {
    seatId
    seatTypeDescription
    image {
      url
      name
    }
    charge {
      __typename
      ... on SeatChargePerTable {
        price
      }
      ... on SeatChargePerPerson {
        price
      }
    }
    ...SeatForLabel
  }
`)

type Params = {
  restaurantId: RestaurantId
  planId: PlanId
  visitorsCount?: number
  visitDate?: DateText
  visitTime?: HourMinute
}
export const planSeatsParams = atom(undefined as undefined | Params)

const planSeats$ = atomWithQuery((get) => ({
  queryKey: ['planSeats', get(planSeatsParams)],
  queryFn: async function (): Promise<FragmentType<typeof PlanSeat>[]> {
    const query = new URLSearchParams({
      v: JSON.stringify(get(planSeatsParams)),
    })
    const result = await fetch(`/api/v2/plan-seats?${query}`)
    return await result.json()
  },
  enabled: get(planSeatsParams) !== undefined,
  staleTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
  initialData: [],
}))

export function usePlanSeats({
  restaurantId,
  planId,
  visitorsCount,
  visitDate,
  visitTime,
}: Params) {
  const [, setParams] = useAtom(planSeatsParams)
  useEffect(() => {
    setParams({ restaurantId, planId, visitorsCount, visitDate, visitTime })
  }, [restaurantId, planId, visitorsCount, visitDate, visitTime, setParams])

  const { data } = useAtomValue(planSeats$)
  // initialData に空配列を設定しているので undefined にはならない
  return useMemo(() => mergeSeats(data!.map((s) => getFragmentData(PlanSeat, s))), [data])
}

// 同類の席をまとめる
// 以下条件をすべて満たす場合は、同類と見なす
// - 席表示名（seatTypeLabel, attributes, smokingAllowed）が一致
// - 席備考（seatTypeDescription）が一致
// - チャージ料（charge）が一致
// - 画像URL（image.url）が一致 または 画像（image）そのものが NULL
function mergeSeats(seats: ResultOf<typeof PlanSeat>[]) {
  const uniqueMap: Map<string, ResultOf<typeof PlanSeat>> = new Map()

  for (const s of seats) {
    const key = `${seatLabel(s)}_${s.seatTypeDescription}_${
      s.charge.__typename === 'SeatChargeNoCharge'
        ? s.charge.__typename
        : s.charge.__typename + s.charge.price
    }`
    const existing = uniqueMap.get(key)

    if (existing === undefined) {
      // まだ存在しない場合は追加
      uniqueMap.set(key, s)
    } else if (existing.image === null && s.image !== null) {
      // 既存の席が image なしの場合は上書き
      uniqueMap.set(key, s)
    } else if (existing.image !== null && s.image !== null && existing.image.url !== s.image.url) {
      // 既存の席と image.url が違う場合は追加
      uniqueMap.set(`${key}_${s.image.url}`, s)
    }
  }

  return Array.from(uniqueMap.values())
}
if (import.meta.vitest) {
  const { describe, test, expect } = import.meta.vitest
  const seat1 = {
    seatTypeDescription: '2時間制です',
    charge: { __typename: 'SeatChargeNoCharge' },
    image: {
      url: 'http://example.com/seat.jpg',
    },
    seatTypeLabel: '完全個室',
    attributes: ['SOFA', 'WINDOW_SEAT'],
    smokingAllowed: false,
  } as unknown as ResultOf<typeof PlanSeat>
  describe('mergeSeats', () => {
    test('すべて一致する席は同類', () => {
      expect(mergeSeats([seat1, seat1]).length).toBe(1)
    })
    test('画像がNULLでそれ以外すべて一致する席は同類', () => {
      const seat2 = {
        ...seat1,
        image: null,
      } as unknown as ResultOf<typeof PlanSeat>
      expect(mergeSeats([seat1, seat2]).length).toBe(1)
    })
    test('画像URLが異なる場合は別の席と見なす', () => {
      const seat2 = {
        ...seat1,
        image: {
          url: 'http://example.com/seat2.jpg',
        },
      } as unknown as ResultOf<typeof PlanSeat>
      expect(mergeSeats([seat1, seat2]).length).toBe(2)
    })
  })
}
